import { Controller } from "@hotwired/stimulus"
import { get, patch } from "@rails/request.js"

export default class extends Controller {
  static targets = ["form", "list", "countBubble", "count", "dropdown", "dropdownBody"]

  connect() {
    this.setupFormHandlers()
    this.initializeNotificationCount()
    this.observeNotifications()
  }

  disconnect() {
    if (this.notificationObserver) {
      this.notificationObserver.disconnect()
    }
  }

  observeNotifications() {
    // Watch for Turbo Stream updates to the notifications container
    const systemNotificationsContainer = document.getElementById('system-notifications')
    if (!systemNotificationsContainer) return

    this.notificationObserver = new MutationObserver(() => {
      this.updateNotificationCountFromDOM()
      // Refresh dropdown if it's open
      if (this.hasDropdownTarget && !this.dropdownTarget.classList.contains('d-none')) {
        this.loadNotifications()
      }
    })

    this.notificationObserver.observe(systemNotificationsContainer, {
      childList: true,
      subtree: true
    })
  }

  setupFormHandlers() {
    if (this.hasFormTarget) {
      this.formTarget.addEventListener('ajax:success', (event) => {
        this.formTarget.reset()
        this.showAlert(event.detail[0].message, 'success')
      })

      this.formTarget.addEventListener('ajax:error', (event) => {
        const errors = event.detail[0].errors
        this.showAlert(errors.join(', '), 'danger')
      })
    }
  }

  async deactivateNotification(event) {
    event.preventDefault()

    const notificationId = event.target.getAttribute('data-notification-id')

    if (confirm('Are you sure you want to deactivate this notification?')) {
      try {
        const response = await patch(`/internal/notifications/${notificationId}/deactivate`, {
          responseKind: 'json'
        })

        if (response.ok) {
          const data = await response.json
          if (data.success) {
            this.showAlert(data.message, 'success')
          } else {
            this.showAlert('Failed to deactivate notification', 'danger')
          }
        } else {
          this.showAlert('Failed to deactivate notification', 'danger')
        }
      } catch (error) {
        console.error('Error:', error)
        this.showAlert('An error occurred while deactivating the notification', 'danger')
      }
    }
  }

  isOrganizerPage() {
    return document.body.classList.contains('organizer-index') ||
           document.body.classList.contains('organizer-edit') ||
           document.body.classList.contains('organizer-show') ||
           document.location.pathname.startsWith('/organizer') ||
           document.location.pathname.startsWith('/internal')
  }

  showAlert(message, type) {
    const alertDiv = document.createElement('div')
    alertDiv.className = `alert alert-${type} alert-dismissible fade show`
    alertDiv.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `

    const container = document.querySelector('.container')
    if (container) {
      container.insertBefore(alertDiv, container.firstChild)
    }

    setTimeout(() => {
      if (alertDiv.parentNode) {
        alertDiv.remove()
      }
    }, 5000)
  }

  showToast(message, type = 'info') {
    const toast = document.createElement('div')
    toast.className = `alert alert-${type} alert-dismissible fade show position-fixed`
    toast.style.cssText = 'top: 20px; right: 20px; z-index: 9999; max-width: 300px;'
    toast.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `

    document.body.appendChild(toast)

    setTimeout(() => {
      if (toast.parentNode) {
        toast.remove()
      }
    }, 5000)
  }

  initializeNotificationCount() {
    this.updateNotificationCountFromDOM()
  }

  updateNotificationCountFromDOM() {
    if (this.hasCountTarget && this.hasCountBubbleTarget) {
      // Get current notification count from system notifications on page
      const notificationElements = document.querySelectorAll('#system-notifications [data-notification-id]')
      const count = notificationElements.length
      this.updateNotificationCount(count)
    }
  }

  updateNotificationCount(count) {
    if (this.hasCountTarget && this.hasCountBubbleTarget) {
      this.countTarget.textContent = count

      if (count > 0) {
        this.countBubbleTarget.classList.remove('d-none')
      } else {
        this.countBubbleTarget.classList.add('d-none')
      }
    }
  }

  toggleDropdown(event) {
    event.preventDefault()

    if (this.hasDropdownTarget) {
      const isVisible = !this.dropdownTarget.classList.contains('d-none')

      if (isVisible) {
        this.hideDropdown()
      } else {
        this.showDropdown()
        this.loadNotifications()
      }
    }
  }

  showDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.remove('d-none')
      document.addEventListener('click', this.hideDropdownOnOutsideClick.bind(this))
    }
  }

  hideDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add('d-none')
      document.removeEventListener('click', this.hideDropdownOnOutsideClick.bind(this))
    }
  }

  hideDropdownOnOutsideClick(event) {
    if (this.hasDropdownTarget && !this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  loadNotifications() {
    if (this.hasDropdownBodyTarget) {
      // Get all notifications (both active and dismissed)
      this.fetchAllNotifications()
    }
  }

  async fetchAllNotifications() {
    try {
      const response = await get('/notifications/all.json', {
        responseKind: 'json'
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json
      if (data.notifications && data.notifications.length > 0) {
        let notificationsHtml = ''
        data.notifications.forEach(notification => {
          const isDismissed = notification.dismissed
          const dismissedClass = isDismissed ? 'dismissed' : ''
          const dismissedLabel = isDismissed ? '<span class="dismissed-label">Dismissed</span>' : ''

          notificationsHtml += `
            <div class="notification-item ${dismissedClass}" data-notification-type="${notification.type}" data-notification-id="${notification.id}">
              <div class="notification-content">
                <div class="notification-header">
                  <strong>${notification.type_text}</strong>
                  <div class="notification-meta">
                    ${dismissedLabel}
                    <span class="notification-time">${notification.time_ago}</span>
                  </div>
                </div>
                <p class="notification-message">${notification.message}</p>
              </div>
            </div>
          `
        })
        this.dropdownBodyTarget.innerHTML = notificationsHtml
      } else {
        this.dropdownBodyTarget.innerHTML = `
          <div class="notification-item">
            <div class="empty-state">
              <p>No notifications</p>
            </div>
          </div>
        `
      }
    } catch (error) {
      console.error('Error fetching notifications:', error)
      // Fallback to current system notifications
      this.loadNotificationsFromDOM()
    }
  }

  loadNotificationsFromDOM() {
    const systemNotifications = document.querySelectorAll('#system-notifications [data-notification-id]')

    if (systemNotifications.length === 0) {
      this.dropdownBodyTarget.innerHTML = `
        <div class="notification-item">
          <div class="empty-state">
            <p>No notifications</p>
          </div>
        </div>
      `
    } else {
      let notificationsHtml = ''
      systemNotifications.forEach(notification => {
        // Extract the notification type and emoji
        const strongElement = notification.querySelector('strong')
        const typeText = strongElement ? strongElement.textContent.trim() : ''

        // Extract the main message (everything after the strong element)
        const flexGrowDiv = notification.querySelector('.flex-grow-1')
        let message = ''
        if (flexGrowDiv) {
          // Clone the element to manipulate it
          const clone = flexGrowDiv.cloneNode(true)
          // Remove the strong element and small element to get just the message
          const strongToRemove = clone.querySelector('strong')
          const smallToRemove = clone.querySelector('small')
          if (strongToRemove) strongToRemove.remove()
          if (smallToRemove) smallToRemove.remove()
          message = clone.textContent.trim()
        }

        // Get the notification type for styling
        const notificationId = notification.getAttribute('data-notification-id')
        const alertClasses = notification.className
        let notificationType = 'info'
        if (alertClasses.includes('alert-danger')) notificationType = 'danger'
        else if (alertClasses.includes('alert-warning')) notificationType = 'warning'

        // Get and format the timestamp
        const createdAt = notification.getAttribute('data-created-at')
        let timeAgo = ''
        if (createdAt) {
          const date = new Date(createdAt)
          timeAgo = this.formatTimeAgo(date)
        }

        notificationsHtml += `
          <div class="notification-item" data-notification-type="${notificationType}">
            <div class="notification-content">
              <div class="notification-header">
                <strong>${typeText}</strong>
                ${timeAgo ? `<span class="notification-time">${timeAgo}</span>` : ''}
              </div>
              <p class="notification-message">${message}</p>
            </div>
          </div>
        `
      })
      this.dropdownBodyTarget.innerHTML = notificationsHtml
    }
  }

  formatTimeAgo(date) {
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)

    if (diffInSeconds < 60) {
      return 'just now'
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60)
      return `${minutes}m ago`
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600)
      return `${hours}h ago`
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400)
      return `${days}d ago`
    } else {
      // For older notifications, show the actual date
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }

  async dismissNotification(event) {
    event.preventDefault()

    const notificationId = event.target.getAttribute('data-notification-id')
    if (!notificationId) return

    try {
      const response = await patch(`/notifications/${notificationId}/dismiss`, {
        responseKind: 'json'
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json
      if (data.success) {
        // Remove the banner immediately so dismissal works without a reload
        // (the server's Turbo broadcast also targets #notification_<id>, but we
        // don't rely on the round-trip here for responsiveness).
        document.getElementById(`notification_${notificationId}`)?.remove()

        // Refresh dropdown if open
        if (this.hasDropdownTarget && !this.dropdownTarget.classList.contains('d-none')) {
          this.loadNotifications()
        }
      } else {
        console.error('Failed to dismiss notification:', data.message)
        alert(`Failed to dismiss notification: ${data.message}`)
      }
    } catch (error) {
      console.error('Error dismissing notification:', error)
      alert(`Error dismissing notification: ${error.message}`)
    }
  }
}